import React, { useEffect, useState } from "react";
import { ICON_BASE_PATH } from "@/app/utils/config";
import { TextBox, type TextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { t } from "i18next";

interface EmailInputProps {
  id?: string;
  fieldName?: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onValidate?: (field: string, isValid: boolean) => void;
  disable?: boolean;
  triggerValidation?: boolean;
  max?: number;
  minLength?: number;
  mobileError?: string;
  starlable?: boolean;
  otherFieldHasValue?: boolean;
  showStarLabel?: boolean;
}

const EmailInput: React.FC<EmailInputProps> = ({
  id,
  fieldName = "emailId",
  label,
  value,
  onChange,
  onValidate,
  disable = false,
  triggerValidation,
  max = 100,
  minLength = 5,
  mobileError,
  starlable = false,
  otherFieldHasValue = false,
}) => {
  const [validationMessage, setValidationMessage] = useState("");
  const [touched, setTouched] = useState(false);

  // ............................... Sanitize ................................
  const sanitize = (s: string) =>
    String(s ?? "")
      .toLowerCase()
      .replaceAll(/[^a-z0-9@._%+-]/g, "");

  // ............................... Validate ................................
  const validate = (email: string): string => {
    let msg = "";

    if (starlable) {
      if (!email.trim()) {
        msg = `${t("please_enter_the")} ${label}`;
        return msg;
      }
    } else if (!email.trim()) {
        if (otherFieldHasValue) {
          return "";
        } else {
          msg = `${t("please_enter_the")} ${label}`;
          return msg;
        }
      }

    if (email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        msg = `${t("please_enter_the")} ${t("valid")} ${label}`;
      } else if (email.length < minLength) {
        msg = `${label} ${t("must_be_at_least")} ${minLength} ${t("characters")}.`;
      } else if (email.length > max) {
        msg = `${label} ${t("cannot_exceed")} ${max} ${t("characters")}`;
      }
    }

    return msg;
  };

  // ............................... handleChange ................................
  const handleChange = (e: TextBoxChangeEvent) => {
    let newValue = sanitize(String(e.value ?? ""));
    if (max && newValue.length > max) newValue = newValue.slice(0, max);
    onChange(newValue);

    if (touched) {
      const msg = validate(newValue);
      setValidationMessage(msg);
      onValidate?.(fieldName, !msg);
    }
  };

  // ............................... handleBlur ................................
  const handleBlur = () => {
    setTouched(true);
    const msg = validate(value);
    setValidationMessage(msg);
    onValidate?.(fieldName, !msg);
  };

  const isInvalid = (!!validationMessage || !!mobileError) && touched;
  const displayError = mobileError || validationMessage;
  const showRequiredStar = starlable || !otherFieldHasValue;

  // ............................... useEffect ................................
  useEffect(() => {
    if (mobileError) {
      setTouched(true);
    }
  }, [mobileError]);

  useEffect(() => {
    if (triggerValidation) {
      setTouched(true);
      const msg = validate(value);
      setValidationMessage(msg);
      onValidate?.(fieldName, !msg);
    }
  }, [triggerValidation]);

  useEffect(() => {
    if (touched || triggerValidation) {
      const msg = validate(value);
      setValidationMessage(msg);
      onValidate?.(fieldName, !msg);
    }
  }, [otherFieldHasValue, starlable]);

  return (
    <div className="gx_input_container" id={id}>
      <label
        className={`input-label gx-font-semibold ${value ? "floating" : ""}`}
      >
        {t(label)}
        {showRequiredStar && (
          <span
            className={`gx_input_star ${
              value ? "gx_input_star_float" : ""
            }`}
          >
            *
          </span>
        )}
      </label>

      <div className={`gx_input_cont ${isInvalid ? "invalid" : "valid"}`}>
        <img src={`${ICON_BASE_PATH}email-svg.svg`} alt="mail" />

        <TextBox
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          type="text"
          autoComplete="off"
          disabled={disable}
          className="gx-textbox-css"
          maxLength={max}
          aria-invalid={isInvalid}
        />
      </div>

      {isInvalid && displayError && (
        <div className="gx_input_validationmessage gx-font-regular">
          {displayError}
        </div>
      )}
    </div>
  );
};

export default EmailInput;
